import React from 'react';
import { Box, FormControlLabel, FormGroup, Stack, Typography, useTheme } from '@mui/material';
import { ChangeEvent } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { IconMicrophone } from '@tabler/icons-react';

import CustomSwitch from '@shared/components/forms/theme-elements/CustomSwitch';
import CustomTextField from '@shared/components/forms/theme-elements/CustomTextField';
import { AgentToolsFormValue } from '@features/Admin/Agent/model/types';
import { ExpandableSettingsBlock } from './SettingsBlocks';
import { ToolPanelProps } from './types';

export const VoiceAnalysisSettingsPanel: React.FC<ToolPanelProps> = ({
  isReadOnly,
  expanded,
  onExpandedChange,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const { control } = useFormContext<AgentToolsFormValue>();

  return (
    <ExpandableSettingsBlock
      id="gender"
      expanded={expanded.gender}
      onChange={(nextExpanded) => onExpandedChange('gender', nextExpanded)}
      header={
        <Stack direction="row" alignItems="center" justifyContent="space-between" width="100%">
          <Stack
            direction="row"
            alignItems="center"
            spacing={1}
            sx={{
              'svg path': {
                stroke: theme.palette.getContrastText(theme.palette.background.default),
              },
            }}>
            <IconMicrophone width={16} />
            <Typography fontSize="16px" fontWeight={600}>
              {t('campaignPage.genderDetectionCheckbox')}
            </Typography>
          </Stack>
          <FormGroup>
            <FormControlLabel
              sx={{ marginRight: 0 }}
              control={
                <Controller
                  name="genderDetectionTool.enabled"
                  control={control}
                  render={({ field }) => (
                    <CustomSwitch
                      {...field}
                      checked={Boolean(field.value)}
                      disabled={isReadOnly}
                      inputProps={{
                        'aria-label': t('campaignPage.genderDetectionCheckbox'),
                      }}
                      onClick={(event: React.MouseEvent) => event.stopPropagation()}
                      onChange={(e: ChangeEvent<HTMLInputElement>) => {
                        field.onChange(e.target.checked);
                        onExpandedChange('gender', e.target.checked);
                      }}
                    />
                  )}
                />
              }
              label=""
            />
          </FormGroup>
        </Stack>
      }>
      <Controller
        name="genderDetectionTool.enabled"
        control={control}
        render={({ field: enabledField }) => (
          <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <Controller
                name="genderDetectionTool.confidenceThreshold"
                control={control}
                render={({ field }) => (
                  <CustomTextField
                    {...field}
                    label={t('campaignPage.genderDetection.confidenceThreshold')}
                    type="number"
                    inputProps={{ step: 0.01, min: 0, max: 1 }}
                    fullWidth
                    disabled={isReadOnly || !enabledField.value}
                    value={field.value ?? ''}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                      field.onChange(
                        e.target.value === '' ? undefined : parseFloat(e.target.value),
                      );
                    }}
                  />
                )}
              />
              <Controller
                name="genderDetectionTool.durationThreshold"
                control={control}
                render={({ field }) => (
                  <CustomTextField
                    {...field}
                    label={t('campaignPage.genderDetection.durationThreshold')}
                    type="number"
                    inputProps={{ step: 1, min: 0 }}
                    fullWidth
                    disabled={isReadOnly || !enabledField.value}
                    value={field.value ?? ''}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                      field.onChange(
                        e.target.value === '' ? undefined : parseInt(e.target.value, 10),
                      );
                    }}
                  />
                )}
              />
            </Box>
            <Controller
              name="genderDetectionTool.behaviorTemplate"
              control={control}
              render={({ field }) => (
                <CustomTextField
                  {...field}
                  label={t('campaignPage.genderDetection.behaviorTemplate')}
                  fullWidth
                  multiline
                  rows={3}
                  disabled={isReadOnly || !enabledField.value}
                  value={field.value ?? ''}
                />
              )}
            />
          </Box>
        )}
      />
    </ExpandableSettingsBlock>
  );
};
